import * as React from 'react'

import { Box, Button, MenuItem, Select, Link } from '@mui/material'
import { INode } from 'shared/types/tree'
import { getGradeId, nodesOfTypeWithData } from '../utility/TreeHelpers'
import {
  httpAndDataUrls,
  ImageStatus,
  imageUrlStatus,
  partnerKeyForTree,
  TreeImageContext,
} from './utility'
import { doInBatches, downloadImportUrlMap } from '../../utility'
import { exportCsv } from 'shared/utils'
import { createIdMap } from '../version_mapping/utility'
import { closestNavigableParent, getNodeUrl } from '../utility/TreeNavigation'

import { getCurriculaContentFolder } from '../ingestion/Utility'
import { ExternalImageReplace } from './ExternalImageReplace'
import { currentEnvironment, Environment } from 'infrastructure/api'
import { useSubjectForTree } from '../../api/subjects.hooks'
import { useGetTreeById } from '../../reducers/trees_hooks'

export function TreeImagesVerification({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const { tree } = useGetTreeById({ treeId, versionId })

  if (!tree) {
    return <Box>Loading Tree...</Box>
  }
  return <TreeImagesVerificationData tree={tree} />
}

function TreeImagesVerificationData({ tree }: { tree: INode }) {
  const [externalUrls, setExternalUrls] = React.useState<string[]>()
  const [internalUrls, setInternalUrls] = React.useState<string[]>()
  const [verified, setVerified] = React.useState<string[]>([])
  const [invalid, setInvalid] = React.useState<string[]>([])
  const [improperlyFormatted, setImproperlyFormatted] = React.useState<
    string[]
  >([])
  const [failedMathFormat, setFailedMathFormat] = React.useState<string[]>([])
  const [blocked, setBlocked] = React.useState<string[]>([])
  const [insecure, setInsecure] = React.useState<string[]>([])
  const [data, setData] = React.useState<string[]>([])
  const [selectedGroup, setSelectedGroup] = React.useState<string[]>()
  const [selectedTitle, setSelectedTitle] = React.useState('')
  const [didIt, setDidIt] = React.useState(false)
  const [existingImages, setExistingImages] = React.useState<{
    [key: string]: string
  }>()
  const [SVGTypeMismatch, setSVGTypeMismatch] = React.useState<string[]>([])
  const [environment, setEnvironment] = React.useState<Environment>(
    currentEnvironment() || Environment.Production,
  )
  const [idMap] = React.useState(createIdMap(tree))

  const partnerKey = partnerKeyForTree(tree)
  const subject = useSubjectForTree(tree)
  const gradeId = String(getGradeId(tree))

  React.useEffect(() => {
    const { internalUrls, externalUrls, data, malformedMathUrls } =
      httpAndDataUrls(tree)
    setFailedMathFormat(malformedMathUrls)
    setData(data)
    setExternalUrls(externalUrls)
    setInternalUrls(internalUrls)
  }, [tree])

  React.useEffect(() => {
    if (!subject) return
    getCurriculaContentFolder({
      partnerId: partnerKey,
      currId: subject.subject_name,
      gradeCode: gradeId,
      envOverride: environment,
    }).then(setExistingImages)
  }, [partnerKey, subject, gradeId, environment])

  async function doIt() {
    if (!externalUrls || !internalUrls) return
    const testImage = async (url: string) => {
      const setter = {
        [ImageStatus.Data]: setData,
        [ImageStatus.Valid]: setVerified,
        [ImageStatus.Invalid]: setInvalid,
        [ImageStatus.Insecure]: setInsecure,
        [ImageStatus.Blocked]: setBlocked,
        [ImageStatus.ImproperFormat]: setImproperlyFormatted,
        [ImageStatus.FailedMathFormat]: setFailedMathFormat,
        [ImageStatus.SVGTypeMismatch]: setSVGTypeMismatch,
      }[await imageUrlStatus(url)]
      setter((prev) => [...prev, url])
    }
    setDidIt(true)
    return doInBatches([...externalUrls, ...internalUrls], testImage)
  }
  function onDoItPress() {
    setInsecure([])
    setVerified([])
    setInvalid([])
    setImproperlyFormatted([])
    doIt().catch()
  }

  function setSelected(title: string, group: string[]) {
    setSelectedTitle(title)
    setSelectedGroup(group)
  }

  async function imageImportScript(...externalUrls: string[]) {
    if (!subject) return
    return downloadImportUrlMap({
      externalUrls,
      partnerKey,
      subject: subject.subject_name,
      gradeId,
      envOverride: environment,
    })
  }

  if (!internalUrls || !externalUrls || !existingImages)
    return <Box>Please Wait...</Box>

  return (
    <TreeImageContext.Provider value={{ imageImportScript, idMap, tree }}>
      <Box>Tree Image Verification</Box>
      <Box>
        <Select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as Environment)}
        >
          {Object.entries(Environment).map(([label, value]) => (
            <MenuItem key={label} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box>
        <Button disabled={didIt} onClick={onDoItPress}>
          Do It
        </Button>
      </Box>
      <Box>{internalUrls.length + externalUrls.length} Images</Box>
      <Box>
        {(
          [
            ['Valid', verified],
            ['Invalid', invalid],
            ['Data', data],
            ['Insecure', insecure],
            ['Blocked', blocked],
            ['Improperly Formatted', improperlyFormatted],
            ['Type Mismatch (svg)', SVGTypeMismatch],
          ] as [string, string[]][]
        ).map(([title, arr]) => {
          if (!arr.length) return null
          return (
            <Button
              key={title}
              onClick={() => setSelected(title, arr)}
            >{`${arr.length} ${title}`}</Button>
          )
        })}
        <Box>
          <Button
            key={'Improper Math Format'}
            onClick={() =>
              setSelected('Improper Math Format', failedMathFormat)
            }
          >{`${failedMathFormat.length} ${'Improper Math Format'}`}</Button>
        </Box>
      </Box>
      <ExternalImageReplace
        allImageUrls={externalUrls}
        existingImages={existingImages}
      />
      <UrlGroupView title={selectedTitle} urls={selectedGroup} />
    </TreeImageContext.Provider>
  )
}

function UrlGroupView({ title, urls }: { title: string; urls?: string[] }) {
  const [offset, setOffset] = React.useState(0)
  const [nodeUrls, setNodeUrls] = React.useState<{
    [url: string]: { title: string; count: number }
  }>({})
  const [loading, setLoading] = React.useState(false)

  const { idMap, tree } = React.useContext(TreeImageContext)

  const nextSet = React.useCallback(
    (start: number) => {
      if (!urls) return {}
      const toProcess = urls.slice(start * 50, (start + 1) * 50)
      return toProcess.reduce(
        (acc: { [url: string]: { title: string; count: number } }, url) => {
          const [withUrl] = nodesOfTypeWithData({
            tree,
            types: [],
            searches: [url],
          })
          const node = closestNavigableParent(withUrl, idMap)
          const nodeUrl = getNodeUrl(node, idMap)
          if (!(nodeUrl in acc)) {
            acc[nodeUrl] = { title: node.title, count: 0 }
          }
          acc[nodeUrl].count++
          return acc
        },
        {},
      )
    },
    [urls, tree, idMap],
  )

  React.useEffect(() => {
    setNodeUrls({})
    setOffset(0)
  }, [urls])

  React.useEffect(() => {
    if (offset === -1) return
    const next = nextSet(offset)
    setLoading(true)
    if (!Object.keys(next).length) {
      setOffset(-1)
      return
    }
    setNodeUrls((prev) => {
      const combined = Object.entries(next).reduce(
        (acc, [key, { count, title }]) => {
          if (key in acc) {
            acc[key].count += count
          } else {
            acc[key] = { count, title }
          }
          return acc
        },
        { ...prev },
      )
      setLoading(false)
      return combined
    })
  }, [offset, idMap, nextSet])

  const loadAll = () => {
    let timeoutId: any
    const increment = () => {
      if (offset === -1) {
        clearTimeout(timeoutId)
        return
      }
      setOffset((prev) => {
        if (prev === -1) return prev
        return prev + 1
      })
      timeoutId = setTimeout(increment, 500)
    }
    increment()
  }

  const exportContent = () => {
    const data = Object.entries(nodeUrls).reduce(
      (acc, [url, { title: nodeTitle, count }]) => {
        acc.push([String(count), `"${nodeTitle.replaceAll('"', '')}"`, url])
        return acc
      },
      [['# Affected', 'Node Name', 'Link']],
    )
    exportCsv(data)
  }

  if (!urls || !tree) return null

  return (
    <Box>
      <Box>
        <strong>{title}</strong>
        <Button
          disabled={loading || offset === -1}
          onClick={() => setOffset(offset + 1)}
        >
          +
        </Button>
        {offset !== -1 && <Button onClick={loadAll}>Load All</Button>}
        {offset === -1 && <Button onClick={exportContent}>Export</Button>}
      </Box>
      <Box>
        {Object.entries(nodeUrls).map(([url, { title: nodeTitle, count }]) => (
          <Box key={url}>
            {`${count} ${title} - `}
            <Link href={url} target={'_blank'}>
              {`${nodeTitle}`}
            </Link>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
